import {
    useContext, useEffect, useRef, useState,
} from 'react';
import {
    InputGroup, Input as ReactstrapInput,
} from 'reactstrap';
import cls from 'classnames';
import { cleanText as stringsCleanText } from '@s20/common/util/string';
import { FormContext, FormLabel } from '../FormElements';
import { TipAlert } from '../../TipAlert';
import './Input.scss';

export const Input = ({
    type = 'text',
    required = false,
    error,
    id,
    name,
    label,
    helpMessage = '',
    className,
    formGroupHidden = false,
    formGroupClassName = '',
    onChange = () => { },
    value = '',
    helperText = '',
    cleanText = true,

    // Input addons
    startAddon = null,
    startAddonContained = true,
    startAddonClassName,

    endAddon = null,
    endAddonContained = true,
    endAddonClassName,

    // Max length + display
    maxLength,
    displayMaxLength = true,

    // Numeric-only
    wholeNumber,
    preventNegative,

    TipComponentProps = null,

    innerRef,
    size,

    ...restProps
}) => {
    const fieldId = id || name;
    const context = useContext(FormContext);

    let fieldValue = value;
    let fieldOnChange = onChange;

    if (context && context.values && fieldId) {
        fieldValue = context.values[name || id];
        fieldOnChange = (e, v) => {
            onChange(e, v);
            context.onChange(e, v);
        };
    }

    if (cleanText) {
        const nextOnChange = fieldOnChange;
        fieldOnChange = (e, v) => {
            if (e?.target && typeof e.target.value === 'string') {
                e.target.value = stringsCleanText(e.target.value);
            }
            nextOnChange(e, v);
        };
    }

    const [charCount, setCharCount] = useState(String(fieldValue).length);
    const prevDefaultValueRef = useRef(fieldValue);

    useEffect(() => {
        if (fieldValue !== prevDefaultValueRef.current) {
            prevDefaultValueRef.current = fieldValue;
            setCharCount(fieldValue?.length ?? 0);
        }
    }, [fieldValue]);

    const combinedClass = cls(type === 'file' ? 'form-control-file' : 'form-control', className, {
        inputContainedStart: Boolean(startAddon) && !!startAddonContained,
        inputContainedEnd: Boolean(endAddon) && !!endAddonContained,
    });

    const inputProps = {
        name,
        required,
        type,
        value: fieldValue,
        onChange: fieldOnChange,
        maxLength,
        className: combinedClass,
        id: fieldId,
        innerRef,
        bsSize: size,
        ...restProps,
    };

    return (
        <div
            hidden={formGroupHidden}
            className={cls('form-group', 'form-group-input', !label && 'mb-0', formGroupClassName)}
        >
            {Boolean(label) && (
                <FormLabel
                    id={`${fieldId}_label`}
                    htmlFor={fieldId}
                    text={label}
                    required={required}
                    helpMessage={helpMessage}
                />
            )}

            <div className="form-group-input-container">
                {displayMaxLength && maxLength && (
                    <small className="input-character-label text-muted">
                        {charCount}/{maxLength}
                    </small>
                )}

                {(startAddon || endAddon) ? (
                    <InputGroup className={cls('input-group position-relative input-group-center', size === 'lg' && 'input-group-lg')}>
                        {Boolean(startAddon) && !startAddonContained && (
                            <div className="input-group-prepend">
                                {startAddon}
                            </div>
                        )}

                        <ReactstrapInput {...inputProps} />

                        {Boolean(startAddon) && Boolean(startAddonContained) && (
                            <div
                                className={cls('input-group-prepend', startAddonClassName || '', {
                                    containedStart: startAddonContained,
                                })}
                            >
                                {startAddon}
                            </div>
                        )}

                        {Boolean(endAddon) && (
                            <div
                                className={cls('input-group-append', endAddonClassName || '', {
                                    containedEnd: endAddonContained,
                                })}
                            >
                                {endAddon}
                            </div>
                        )}

                        {Boolean(error) && (
                            <small className="invalid-feedback text-danger">
                                {error}
                            </small>
                        )}

                        {Boolean(helperText) && (
                            <small className="invalid-feedback helper-text text-muted">
                                {helperText}
                            </small>
                        )}
                    </InputGroup>
                ) : (
                    <>
                        <ReactstrapInput {...inputProps} />

                        {Boolean(error) && (
                            <small className="invalid-feedback text-danger">
                                {error}
                            </small>
                        )}

                        {Boolean(helperText) && (
                            <small className="invalid-feedback helper-text text-muted">
                                {helperText}
                            </small>
                        )}
                    </>
                )}
            </div>

            {Boolean(TipComponentProps) && (
                <TipAlert
                    {...TipComponentProps}
                />
            )}
        </div>
    );
};

export default Input;